<!-- eslint-disable -->
<template>
  <v-container fluid class="pa-0 min-h-screen bg-slate-50 d-flex flex-column">
    <SnackBar ref="snackbar" />
    
    <!-- LOADING STATE -->
    <div v-if="loading" class="flex-grow-1 d-flex flex-column align-center justify-center py-16">
      <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
      <p class="text-slate-500 font-medium">Carregando dados do roteiro...</p>
    </div>

    <div v-else class="flex-grow-1 flex flex-col md:flex-row overflow-hidden md:h-[calc(100vh-64px)]">
      <!-- LEFT SIDEBAR: List of functionalities and progress -->
      <div class="border-b md:border-b-0 md:border-r border-slate-200 bg-white flex flex-col w-full md:w-[320px] md:min-w-[320px] h-auto md:h-full shrink-0">
        <div class="pa-4 border-b border-slate-150">
          <a @click.prevent="goBack" class="d-flex align-center cursor-pointer text-slate-500 hover:text-slate-800 text-sm mb-3">
            <v-icon size="16" class="mr-1">mdi-arrow-left</v-icon>
            Voltar para o Painel
          </a>
          <h2 class="text-lg font-black text-slate-800 onest-font leading-tight">
            {{ roteiro.titulo }}
          </h2>
          <div class="text-xs text-slate-400 mt-1">
            Projeto: <span class="font-bold text-slate-600">{{ projeto.nome }}</span>
          </div>
          <!-- Progress overall -->
          <div class="mt-4">
            <div class="d-flex justify-between text-xs font-bold text-slate-500 mb-1">
              <span>Progresso Geral</span>
              <span>{{ countAvalidadas }}/{{ functionalitiesList.length }}</span>
            </div>
            <v-progress-linear :model-value="progressPercent" color="primary" height="6" rounded bg-color="slate-200"></v-progress-linear>
          </div>
        </div>

        <!-- Desktop: vertical scrollable list (hidden on mobile) -->
        <v-list class="hidden md:flex flex-col overflow-y-auto flex-grow py-2 scroll-container" density="comfortable">
          <v-list-item
            v-for="(func, idx) in functionalitiesList"
            :key="func.id"
            :active="selectedFuncId === func.id"
            color="primary"
            class="px-4 py-3 border-b border-slate-100"
            @click="scrollToFunc(func.id)"
          >
            <template #prepend>
              <v-icon :color="func.validada ? 'success' : 'slate-300'" class="mr-2" size="18">
                {{ func.validada ? 'mdi-check-circle' : 'mdi-circle-slice-8' }}
              </v-icon>
            </template>
            <v-list-item-title class="font-bold text-slate-700 text-sm">
              {{ idx + 1 }}. {{ func.titulo }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-xs text-slate-400 mt-0.5">
              {{ func.validada ? 'Validada' : 'Pendente' }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>

        <!-- Mobile: horizontal scrollable row (hidden on desktop) -->
        <div class="flex md:hidden flex-row overflow-x-auto p-3 gap-2 bg-slate-50/30">
          <div
            v-for="(func, idx) in functionalitiesList"
            :key="func.id"
            :class="[
              'inline-flex items-center px-4 py-2.5 border rounded-xl cursor-pointer transition-all duration-150 shrink-0 select-none text-sm',
              selectedFuncId === func.id
                ? 'bg-blue-50 border-blue-200 text-blue-700 font-extrabold shadow-sm'
                : 'bg-white border-slate-150 hover:border-slate-300 text-slate-600'
            ]"
            @click="scrollToFunc(func.id)"
          >
            <v-icon :color="func.validada ? 'success' : 'slate-300'" size="16" class="mr-2 flex-shrink-0">
              {{ func.validada ? 'mdi-check-circle' : 'mdi-circle-slice-8' }}
            </v-icon>
            <span class="whitespace-nowrap">{{ idx + 1 }}. {{ func.titulo }}</span>
          </div>
        </div>
      </div>

      <!-- RIGHT PANEL: Header details, test credentials and vertical list of all functionalities -->
      <div class="flex-grow-1 bg-slate-50 overflow-y-auto pa-4 pa-md-8 scroll-container">
        <div class="max-w-4xl mx-auto pb-12">
          
          <!-- Roteiro General Info Card -->
          <div class="bg-white rounded-xl border border-slate-200 p-4 p-md-6 mb-6 shadow-sm">
            <h1 class="text-2xl md:text-3xl font-black text-slate-800 onest-font mb-2 leading-tight">
              {{ roteiro.titulo }}
            </h1>
            <div class="text-xs text-slate-500 mb-4 uppercase font-bold tracking-wider">
              Projeto: <span class="text-slate-800 font-black">{{ projeto.nome }}</span>
            </div>
            
            <p class="text-slate-600 text-sm mb-4">
              {{ projeto.descricao || 'Instruções de testes gerais para validação das entregas.' }}
            </p>

            <v-divider class="my-4"></v-divider>

            <!-- Access Link -->
            <div v-if="roteiro.link" class="bg-blue-50/50 border border-blue-100 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div class="d-flex align-center">
                <v-icon color="primary" class="mr-3" size="24">mdi-open-in-new</v-icon>
                <div>
                  <div class="text-xs font-bold text-slate-400 uppercase">Link do ambiente de testes:</div>
                  <a :href="roteiro.link" target="_blank" class="text-sm font-bold text-blue-600 hover:underline break-all">
                    {{ roteiro.link }}
                  </a>
                </div>
              </div>
              <v-btn color="primary" variant="tonal" size="small" class="font-bold text-none hidden md:block" @click="openLink(roteiro.link)">
                Acessar Ambiente
              </v-btn>
            </div>
          </div>

          <!-- Accounts / Credentials Section -->
          <div v-if="contasTeste.length > 0" class="bg-white rounded-xl border border-slate-200 p-4 p-md-6 mb-6 shadow-sm">
            <h3 class="text-lg font-bold text-slate-800 mb-3 d-flex align-center">
              <v-icon color="warning" class="mr-2" size="22">mdi-key-chain</v-icon>
              Credenciais de Acesso para Teste
            </h3>
            <p class="text-sm text-slate-500 mb-4">Utilize as seguintes credenciais para acessar o sistema durante os testes:</p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div v-for="conta in contasTeste" :key="conta.id_conta" class="border border-slate-150 rounded-xl p-4 bg-slate-50 d-flex justify-between align-center">
                <div class="w-full">
                  <div class="flex gap-8">
                    <div>
                      <div class="text-xs text-slate-400 font-bold uppercase">Usuário / Acesso</div>
                      <div class="text-sm font-mono text-slate-800 font-black mt-0.5">{{ conta.usuario_acesso }}</div>
                    </div>
                    <div>
                      <div class="text-xs text-slate-400 font-bold uppercase">Senha</div>
                      <div class="text-sm font-mono text-slate-800 font-black mt-0.5">{{ conta.senha_acesso }}</div>
                    </div>
                  </div>
                  <div v-if="conta.dados_teste" class="mt-4 pt-3 border-t border-slate-200">
                    <div class="text-xs text-slate-400 font-bold uppercase">Instruções / Dados de Teste</div>
                    <div class="text-sm text-slate-700 mt-1 whitespace-pre-line">{{ conta.dados_teste }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- All Functionalities vertically stacked -->
          <div class="flex flex-col gap-6">
            <article
              v-for="(funcionalidade, index) in functionalitiesList"
              :key="funcionalidade.id"
              :id="'func-' + funcionalidade.id"
              class="min-h-[220px] flex flex-col md:grid md:grid-cols-[46px_1fr] border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm"
            >
              <div
                class="bg-[#004984] text-white text-base md:text-[26px] font-extrabold flex items-center justify-center py-2 md:py-0"
              >
                {{ index + 1 }}
              </div>

              <div class="p-4 md:p-[17px_18px] d-flex flex-column justify-between">
                <!-- Header details -->
                <div>
                  <div class="border border-[#d9d9d9] rounded-xl p-3.5 mb-[13px]">
                    <div class="d-flex justify-between align-start mb-2 gap-2">
                      <h3 class="m-0 text-[#2f2f2f] text-[16px] sm:text-[17px] font-extrabold leading-snug">
                        {{ funcionalidade.titulo }}
                      </h3>
                      <v-chip :color="funcionalidade.validada ? 'success' : 'warning'" size="small" class="font-bold text-caption uppercase px-3 py-0.5 shrink-0">
                        {{ funcionalidade.validada ? 'Validada' : 'Não Testada' }}
                      </v-chip>
                    </div>

                    <p class="m-0 text-[#444444] text-[13px] leading-[1.45]">
                      <strong>Objetivo da funcionalidade:</strong>
                      {{ funcionalidade.objetivo }}
                    </p>

                    <p class="m-0 mt-2 text-[#444444] text-[13px] leading-[1.45]" v-if="funcionalidade.comoAcessar">
                      <strong>Como acessar:</strong>
                      {{ funcionalidade.comoAcessar }}
                    </p>
                  </div>

                  <!-- Screenshots / Reference Images -->
                  <div
                    class="grid grid-cols-1 gap-5 mb-[13px]"
                    v-if="funcionalidade.imagens && funcionalidade.imagens.length > 0"
                  >
                    <div
                      class="w-full h-auto min-h-[200px] max-h-[500px] flex items-center justify-center rounded overflow-hidden"
                      v-for="imagem in funcionalidade.imagens"
                      :key="imagem.id_imagem"
                    >
                      <img
                        :src="imagem.link_imagem"
                        style="width: 100%; max-height: 500px; object-fit: contain;"
                      />
                    </div>
                  </div>

                  <!-- Step Instructions ("What to test") -->
                  <div
                    v-if="funcionalidade.descricao && funcionalidade.descricao.trim() !== 'Sem detalhamento específico'"
                    class="min-h-[70px] border border-[#c2cbd6] border-l-[3px] border-l-[#004984] bg-[#f7fafc] rounded p-4 text-[#2d3748] text-[13px] leading-[1.5]"
                  >
                    <strong>O que deve ser testado:</strong>
                    <div class="mt-1">{{ funcionalidade.descricao }}</div>
                  </div>
                </div>

                <!-- Footer action button -->
                <div class="d-flex justify-end mt-4 pt-3 border-t border-slate-100">
                  <div v-if="isCycleActive" class="w-full sm:w-auto">
                    <v-btn
                      color="success"
                      variant="flat"
                      rounded="lg"
                      size="small"
                      class="font-bold text-none shadow-sm w-full sm:w-auto"
                      prepend-icon="mdi-playlist-check"
                      @click="irParaFormulario(funcionalidade.id)"
                    >
                      {{ funcionalidade.validada ? 'Reavaliar Funcionalidade' : 'Avaliar Funcionalidade' }}
                    </v-btn>
                  </div>
                  <div v-else class="w-full sm:w-auto text-right">
                    <v-btn
                      v-if="funcionalidade.validada"
                      color="primary"
                      variant="outlined"
                      rounded="lg"
                      size="small"
                      class="font-bold text-none w-full sm:w-auto"
                      prepend-icon="mdi-eye"
                      @click="irParaFormulario(funcionalidade.id)"
                    >
                      Visualizar Respostas
                    </v-btn>
                    <span v-else class="text-xs text-slate-400 font-semibold italic">
                      Funcionalidade não avaliada
                    </span>
                  </div>
                </div>
              </div>
            </article>
          </div>

        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
/* eslint-disable */
import axios from 'axios';
import SnackBar from '@/components/SnackBar.vue';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  name: 'RoteiroTestador',
  components: {
    SnackBar,
  },
  data() {
    return {
      idRoteiro: null,
      idProjeto: null,
      idUsuario: Number(localStorage.getItem('idUsuario')) || 1,
      loading: false,
      isCycleActive: true,
      selectedFuncId: null,
      projeto: { nome: '', descricao: '' },
      roteiro: { titulo: '', link: '' },
      contasTeste: [],
      functionalitiesList: [],
      // Mock fallback data
      mockRoteirosData: {
        101: {
          id_roteiro: 101,
          titulo: 'Roteiro de Validação - Gestor de Equipes',
          link: 'http://localhost:8080/sisgip',
          is_active: true,
          projeto: { nome: 'SISGIP', descricao: 'Sistema de Gestão de Projetos e Práticas de Integração' },
          contas: [
            { id_conta: 1, usuario_acesso: 'gestor_val@sisgip.gov', senha_acesso: 'Password123' },
          ],
          funcionalidades: [
            { id: 1, titulo: 'Cadastrar Nova Equipe', objetivo: 'Validar o fluxo completo de cadastro de nova equipe incluindo validação de campos obrigatórios.', comoAcessar: 'Acesse o menu lateral, clique em "Equipes" e selecione "Nova Equipe". Preencha os dados e clique em "Salvar".', imagens: [], descricao: 'A tela de cadastro deve validar o nome obrigatório, a sigla e o repositório principal.' },
            { id: 2, titulo: 'Vincular Repositório', objetivo: 'Validar a associação de repositórios do GitHub a um projeto existente.', comoAcessar: 'Abra os detalhes de um projeto, clique em "Repositórios" e depois em "Associar Repositório".', imagens: [], descricao: 'Deve carregar todos os repositórios públicos da organização selecionada.' },
            { id: 3, titulo: 'Adicionar Membros à Equipe', objetivo: 'Testar a inclusão de usuários e definição de papéis (Dev, Scrum Master, QA).', comoAcessar: 'Na página da equipe, clique em "Gerenciar Membros", pesquise o usuário e defina a atuação.', imagens: [], descricao: 'O sistema deve salvar e exibir a atuação correspondente.' },
            { id: 4, titulo: 'Gerenciar Permissões', objetivo: 'Garantir que desenvolvedores não acessem funções administrativas de coordenação.', comoAcessar: 'Tente acessar a página "/gestao-formularios-validacao" com uma conta de desenvolvedor.', imagens: [], descricao: 'O sistema deve bloquear e apresentar mensagem de acesso não autorizado.' },
          ],
        },
        102: {
          id_roteiro: 102,
          titulo: 'Roteiro de Validação - Membro de Equipe (Desenvolvedor)',
          link: 'http://localhost:8080/sisgip',
          is_active: true,
          projeto: { nome: 'SISGIP', descricao: 'Sistema de Gestão de Projetos e Práticas de Integração' },
          contas: [
            { id_conta: 2, usuario_acesso: 'dev_val@sisgip.gov', senha_acesso: 'DevPassword!' },
          ],
          funcionalidades: [
            { id: 5, titulo: 'Visualizar Backlog da Sprint', objetivo: 'Validar a exibição correta dos itens da Sprint atual.', comoAcessar: 'Navegue para "Sprint" no menu principal.', imagens: [], descricao: 'A lista deve exibir as tarefas estimadas e associadas à Sprint ativa.' },
            { id: 6, titulo: 'Atualizar Status de Tarefa', objetivo: 'Mover uma tarefa entre colunas do board (To Do, In Progress, Done).', comoAcessar: 'No board da Sprint, arraste uma tarefa para a coluna seguinte.', imagens: [], descricao: 'Deve salvar no banco de dados e refletir a nova coluna imediatamente.' },
            { id: 7, titulo: 'Registrar Impedimento', objetivo: 'Reportar bloqueios nas tarefas ativas da Sprint.', comoAcessar: 'Clique nos três pontos de uma tarefa e selecione "Registrar Impedimento".', imagens: [], descricao: 'A tarefa deve receber a sinalização e registrar o log do impedimento.' },
          ],
        },
      },
    };
  },
  computed: {
    countAvalidadas() {
      return this.functionalitiesList.filter((f) => f.validada).length;
    },
    progressPercent() {
      return this.functionalitiesList.length > 0 ? (this.countAvalidadas / this.functionalitiesList.length) * 100 : 0;
    },
  },
  created() {
    this.idRoteiro = Number(this.$route.params.idRoteiro);
    this.idProjeto = Number(this.$route.params.idProjeto);
    this.carregarDadosRoteiro();
  },
  mounted() {
    this.checkSaveStatus();
  },
  methods: {
    checkSaveStatus() {
      const status = this.$route.query.saveStatus;
      if (status === 'success') {
        this.$refs.snackbar.showSuccessSnackbar(
          'Sucesso',
          'Avaliação salva com sucesso no servidor!'
        );
        this.$router.replace({ query: {} }).catch(() => {});
      } else if (status === 'error') {
        const msg = this.$route.query.errorMsg || 'Erro desconhecido.';
        this.$refs.snackbar.showErrorSnackbar(
          'Erro',
          `Não foi possível registrar a sua avaliação: ${msg}`
        );
        this.$router.replace({ query: {} }).catch(() => {});
      }
    },
    goBack() {
      this.$router.push('/workspace-convidado');
    },
    scrollToFunc(id) {
      this.selectedFuncId = id;
      const el = document.getElementById(`func-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    openLink(link) {
      window.open(link, '_blank');
    },
    async carregarDadosRoteiro() {
      this.loading = true;
      try {
        const response = await axios.get(
          `${apiUrl}/api/v1/roteiros/${this.idRoteiro}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        );
        const data = response.data;
        this.idProjeto = data.id_projeto;
        this.projeto.nome = data.tb_projeto?.nome || 'Projeto Desconhecido';
        this.projeto.descricao = data.tb_projeto?.descricao || 'Descrição do projeto';
        this.roteiro.titulo = `Roteiro de Validação - ${data.tb_ator?.nome_ator || 'Testador'}`;
        this.roteiro.link = data.link_acesso || '';
        this.isCycleActive = data.tb_escopo_teste?.[0]?.tb_escopo?.tb_validacao?.situacao === 'USUARIOS_TESTANDO';
        
        this.contasTeste = (data.tb_conta_teste || []).filter(
          (c) => c.id_usuario_externo === this.idUsuario
        );

        if (data.tb_escopo_teste && data.tb_escopo_teste.length > 0) {
          this.functionalitiesList = data.tb_escopo_teste.map((escopo, index) => {
            const fid = escopo.id_escopo_teste;
            const feedbackSaved = localStorage.getItem(`feedback_user_${this.idUsuario}_roteiro_${this.idRoteiro}_escopo_${fid}`);
            return {
              id: fid,
              titulo: escopo.tb_escopo?.funcionalidade || `Funcionalidade #${index + 1}`,
              objetivo: escopo.tb_escopo?.objetivo || 'Instrução do teste',
              comoAcessar: escopo.tb_escopo?.como_acessar || 'Não especificado',
              imagens: escopo.tb_escopo?.tb_imagem_acesso || [],
              descricao: escopo.o_que_deve_testar || '',
              validada: !!feedbackSaved,
            };
          });

          if (this.functionalitiesList.length > 0) {
            this.selectedFuncId = this.functionalitiesList[0].id;
          }
        } else {
          this.functionalitiesList = [];
        }
      } catch (error) {
        console.error('Erro ao carregar roteiro do backend, tentando mock:', error);
        
        // Fallback to mock data
        const data = this.mockRoteirosData[this.idRoteiro];
        if (data) {
          this.roteiro.titulo = data.titulo;
          this.roteiro.link = data.link;
          this.isCycleActive = data.is_active;
          this.projeto.nome = data.projeto.nome;
          this.projeto.descricao = data.projeto.descricao;
          this.contasTeste = data.contas;
          
          this.functionalitiesList = data.funcionalidades.map((f) => {
            const feedbackSaved = localStorage.getItem(`feedback_user_${this.idUsuario}_roteiro_${this.idRoteiro}_escopo_${f.id}`);
            return {
              ...f,
              validada: !!feedbackSaved,
            };
          });

          if (this.functionalitiesList.length > 0) {
            this.selectedFuncId = this.functionalitiesList[0].id;
          }
        } else {
          this.roteiro.titulo = 'Roteiro de Validação';
          this.isCycleActive = false;
          this.functionalitiesList = [];
        }
      } finally {
        this.loading = false;
      }
    },
    irParaFormulario(funcId) {
      this.$router.push(`/projects/${this.idProjeto}/roteiros/${this.idRoteiro}/executar/${funcId}/formulario`);
    },
  },
};
</script>

<style scoped>
.onest-font {
  font-family: 'Onest', sans-serif !important;
}
.bg-slate-50 {
  background-color: #f8fafc !important;
}
.grid {
  display: grid;
}
@media (min-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.gap-4 {
  gap: 1rem;
}
.cursor-pointer {
  cursor: pointer;
}
.scroll-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}
.scroll-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.scroll-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
.shrink-0 {
  flex-shrink: 0 !important;
}
</style>
